"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield, Zap, Lock, EyeOff, Globe, Server, Wifi, Key,
  Cpu, HardDrive, RefreshCw, GitBranch, Cloud, Download,
  Monitor, Users, MapPin, Clock, Layers, CheckCircle, Eye,
} from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Shield, Zap, Lock, EyeOff, Globe, Server, Wifi, Key,
  Cpu, HardDrive, RefreshCw, GitBranch, Cloud, Download,
  Monitor, Users, MapPin, Clock, Layers, CheckCircle, Eye,
  shield: Shield, zap: Zap, lock: Lock, "eye-off": EyeOff,
  globe: Globe, server: Server, wifi: Wifi, key: Key,
  cpu: Cpu, "hard-drive": HardDrive, "refresh-cw": RefreshCw,
  "git-branch": GitBranch, cloud: Cloud, download: Download,
  monitor: Monitor, users: Users, "map-pin": MapPin,
  clock: Clock, layers: Layers, "check-circle": CheckCircle, eye: Eye,
};

interface FeatureCard {
  icon?: string;
  title: string;
  description: string;
}

interface FeatureCardsProps {
  title?: string;
  subtitle?: string;
  columns?: 2 | 3 | 4;
  cards: FeatureCard[];
}

const FeatureCards = ({ title, subtitle, columns = 3, cards }: FeatureCardsProps) => {
  if (!cards || cards.length === 0) return null;

  const gridCols = {
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-2 lg:grid-cols-4",
  }[columns] || "md:grid-cols-3";

  return (
    <section className="py-24 px-6 bg-muted">
      <div className="container max-w-5xl mx-auto">
        {/* Header */}
        {(title || subtitle) && (
          <div className="text-center mb-16 space-y-4">
            {title && (
              <BlurFade delay={0.1}>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  {title}
                </h2>
              </BlurFade>
            )}
            {subtitle && (
              <BlurFade delay={0.15}>
                <p className="text-foreground/60 text-lg max-w-2xl mx-auto">
                  {subtitle}
                </p>
              </BlurFade>
            )}
          </div>
        )}

        {/* Cards Grid */}
        <div className={`grid grid-cols-1 ${gridCols} gap-6`}>
          {cards.map((card, i) => {
            const Icon = card.icon ? (ICON_MAP[card.icon] || Shield) : null;
            return (
              <BlurFade key={i} delay={0.15 + i * 0.05}>
                <Card className="border-foreground/5 bg-white hover:shadow-lg transition-all h-full">
                  <CardContent className="p-6 space-y-4">
                    {Icon && (
                      <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                        <Icon size={20} className="text-foreground/70" strokeWidth={1.5} />
                      </div>
                    )}
                    <h3 className="text-lg font-bold text-foreground">
                      {card.title}
                    </h3>
                    <p className="text-foreground/60 leading-relaxed text-sm">
                      {card.description}
                    </p>
                  </CardContent>
                </Card>
              </BlurFade>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeatureCards;
